/***********************************************
ICEMEDX - Developed By ICEMEDX, Inc.
Copyright 2019 ICEMEDX, Inc.
***********************************************/
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { tap, map } from 'rxjs/operators';
import { XmlparseService } from '../xml/xmlparse.service';

import { AlertService } from "../../services/alert/alert.service";
import { report, ReportBarCodeType, reportDataField, ReportDataTypes, reportField, reportObject, ReportObjectType, reportSection, ReportSectionTypes, reportSummationField, reportSystemField, ReportSystemFieldTypes, reportTextField, Report_Alignment, Report_Border_Style, Report_Page_Sizes } from "../../models/reports/report";
import { reportindex } from "../../models/reports/reportindex";
import { IceTypes } from "../../models/icetypes";
import { Iceutility } from "../../services/iceutility";
import { LoginService } from '../../login/login.service';
import { rptobject } from '../../models/reports/subs/rptobject';
import { IcePoint, IceRect } from '../../components/icemultimedia/IceMultimediaStroke';
import { rptsection } from '../../models/reports/subs/rptsection';
import { reportfilter } from '../../models/reports/subs/reportfilter';
import { IceMultimediaPage } from '../../components/icemultimedia/IceMultimediaPage';
import { reportdata } from '../../models/reports/reportdata';

declare var require: any;

export interface IReportListItem {
  report_key: string;
  name: string;
  type: string;
  run_frequency: string;
  description: string;
  source: string;
  modified: any;
  modified_by: string;
}

export class ReportListObject implements IReportListItem {
  report_key: string;
  name: string;
  type: string;
  run_frequency: string;
  description: string;
  source: string;
  modified: any;
  modified_by: string;

  constructor(key: string, name: string,
      type: string,
      run_freq: string,
      desc: string,
      src: string,
      mod: any,
      modby: string) {
    this.report_key = key;
    this.name = name;
    this.type = type;
    this.run_frequency = run_freq;
    this.description = desc;
    this.source = src;
    this.modified = mod;
    this.modified_by = modby;
  }
}

@Injectable()
export class ReportService extends XmlparseService {

  pixelsPerInch: Function = () => {
    const el: HTMLElement = document.createElement('div');
    el.style.width = 'width: 1in;'
    document.body.appendChild(el);
    const dpi = el.offsetWidth;
    document.body.removeChild(el);
    return dpi;
  }

  inch2Pix: Function = (inches: number, perinch: number, scale: number): number =>  {
    if (scale == 0.0)
      scale = 1.0;

    let pixels = inches * (perinch * scale);
    return pixels;
  };

  pix2Inch: Function = (pixels: number, perinch: number, scale: number): number => {
    if (scale == 0.0)
      scale = 1.0;

    let inches: number = pixels / (perinch * scale);
    return inches;
  };


  c_lnUnRealizedPixelPerInch: number = this.pixelsPerInch();
  c_dblUnRealizedScale: number = 1.0;
  c_dblDEFAULTCELL_Y: number = 0.25;
  c_dblDEFAULTPADDING_Y: number = 0.0625;
  c_dblMM_TO_INCH: number = 0.0394;
  c_dblINCH_TO_MM: number = 25.4;
  c_dblFontPointsPerInch: number = 72;

  constructor(private http: HttpClient, private alertSvc: AlertService, private loginSvc: LoginService) {
    super(alertSvc, loginSvc);
  }

  /////////////////////////////
  // API Operations
  public getReports(api: string, act: string, acttype: IceTypes.ACCOUNT_TYPE, keywords: string = null, type: IceTypes.ReportType = IceTypes.ReportType.rtUndefined): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Report service is not configured.";
    if (act == null || act.length == 0) throw "Account input invalid.";
    if (api == null || api.length == 0) throw "API input invalid.";

    if (keywords == null)
      keywords = "";

    type
    let url = envs[currentEnv].icemedx_api + "/" + api + "/default.aspx?domain=" + act + "&data=reports&domaintype=" + acttype.toString() + "&key=&subkeys=" + "keywords:" + keywords + ";type:" + type.toString();
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.parseReports(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public getReport(api: string, act: string, acttype: IceTypes.ACCOUNT_TYPE, key: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Report service is not configured.";
    if (act == null || act.length == 0) throw "Account input invalid.";
    if (key == null || key.length == 0) throw "Key input invalid";
    if (api == null || api.length == 0) throw "API input invalid.";

    let url = envs[currentEnv].icemedx_api + "/" + api +"/default.aspx?domain=" + act + "&data=report&domaintype=" + acttype.toString() + "&key=" + key +"&subkeys=";
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.parseReport(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public getData(api: string, act: string, acttype: IceTypes.ACCOUNT_TYPE, key: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Report service is not configured.";
    if (act == null || act.length == 0) throw "Account input invalid.";
    if (key == null || key.length == 0) throw "Key input invalid";
    if (api == null || api.length == 0) throw "API input invalid.";

    let url = envs[currentEnv].icemedx_api + "/" + api +"/default.aspx?domain=" + act + "&data=reportdata&domaintype=" + acttype.toString() + "&key=" + key + "&subkeys=";
    return this.http.get(url, { responseType: 'text' }).pipe(
      map(
        response => this.parseReportData(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public updateReport(api: string, act: string, acttype: IceTypes.ACCOUNT_TYPE, rpt: report): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Report service is not configured.";
    if (rpt == null) throw "Report object invalid.";
    if (act == null || act.length == 0) throw "Account input invalid.";
    if (api == null || api.length == 0) throw "API input invalid.";

    let url = envs[currentEnv].icemedx_api + "/" + api +"/default.aspx?domain=" + act + "&data=report&domaintype=" + acttype.toString() + "&key=" + rpt.report_key + "&subkeys=";

    let tblname = rpt.gettablename().toLowerCase();
    let data = "{" + JSON.stringify(tblname) + ": " + JSON.stringify(rpt) + "}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.parseReport(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  public removeReportSigner(api: string, act: string, acttype: IceTypes.ACCOUNT_TYPE, rptData: reportdata, reviewerkey: string): Observable<any> {
    let currentEnv: any = require("../../../../../env.json").currentEnv;
    let envs: any = require("../../../../../env.json").environments;
    if (!envs || !currentEnv) throw "Report service is not configured.";
    if (rptData == null) throw "Report data object invalid.";
    if (act == null || act.length == 0) throw "Account input invalid.";
    if (api == null || api.length == 0) throw "API input invalid.";

    let url = envs[currentEnv].icemedx_api + "/" + api + "/default.aspx?domain=" + act + "&data=removesigner&domaintype=" + acttype.toString() + "&key=" + rptData.report_key + "&subkeys=reviewerkey:" + reviewerkey;

    let tblname = rptData.gettablename().toLowerCase();
    let data = "{" + JSON.stringify(tblname) + ": " + JSON.stringify(rptData) + "}";
    return this.http.post(url, data, { responseType: 'text' }).pipe(
      map(
        response => this.parseReportData(response)
      ),
      tap(
        data => this.parseLogReport(url, data),
        error => this.parseLogReport(url, error))
    );
  }

  static getReportTypes(): any {
    var types = [];
    try {
      types.push({ value: IceTypes.ReportType.rtClincialData.toString(), name: ReportService.getReportType(IceTypes.ReportType.rtClincialData) });
      types.push({ value: IceTypes.ReportType.rtScheduling.toString(), name: ReportService.getReportType(IceTypes.ReportType.rtScheduling) });
      types.push({ value: IceTypes.ReportType.rtMedicalCodes.toString(), name: ReportService.getReportType(IceTypes.ReportType.rtMedicalCodes) });
      types.push({ value: IceTypes.ReportType.rtAccountingSummary.toString(), name: ReportService.getReportType(IceTypes.ReportType.rtAccountingSummary) });
    } catch (e) {
      console.log(e);
    }
    return types;
  }

  static getReportType(type: any) {
    switch (<IceTypes.ReportType>Iceutility.toLong(type)) {
      case IceTypes.ReportType.rtClincialData:
        return "Clinical Data";
      case IceTypes.ReportType.rtScheduling:
        return "Scheduling / Admissions";
      case IceTypes.ReportType.rtMedicalCodes:
        return "Medical Codes";
      case IceTypes.ReportType.rtAccountingSummary:
        return "Accounting Summary";
    }
    return "";
  }

  private parseReports(prvData: any) {
    return this.geticexdocumentobjects(prvData, ["REPORT_INDEX"]);
  }

  private parseReport(prvData: any) {
    return this.geticexdocumentobjects(prvData, ["REPORT"]);
  }

  private parseReportData(prvData: any) {
    return this.geticexdocumentobjects(prvData, ["REPORT_DATA"]);
  }

  //////////////////////////
  /// Preview and Printing Operations

  private isItTimeForNewPage(reportObj: report, currentY: number): boolean {
    let sysdata = reportObj.sysdata;
    let rcMargins: IceRect = reportObj.getMargins();
    let bottomPix: number = this.inch2Pix(rcMargins.bottom, sysdata.pixelsPerInch, sysdata.scale);
    let currentYPix: number = this.inch2Pix(currentY, sysdata.pixelsPerInch, sysdata.scale);
    if (currentYPix + bottomPix >= sysdata.pageLimitY)
      return true;

    return false;
  }

  getDocumentSize(paperType: Report_Page_Sizes): IcePoint {
    let ret: IcePoint = new IcePoint();
    if (paperType == Report_Page_Sizes.ps8_5_by_11) {// more sizes are coming
      ret.x = 8.5;
      ret.y = 11.0;
    }
    return ret;
  }

  private complete(reportObj: report, sortfield: number, prevval: any, curval: any): boolean {
    if (sortfield < 0)
      return false;

    return false == this.compareReportFieldData(reportObj, sortfield, prevval, curval);
  }

  private continue(reportObj: report, sortfield: number, prevval: any, curval: any): boolean {
    if (sortfield < 0)
      return false;

    return this.compareReportFieldData(reportObj, sortfield, prevval, curval);
  }

  private compareReportFieldData(reportObj: report, fieldIndex: number, fieldData1: any, fieldData2: any): boolean {
    if (fieldData1 == null || fieldData2 == null) return false;

    let tC: string = reportObj.outputColumns[fieldIndex];
    let fld: reportDataField = reportObj.getReportDataField(tC);
    if (fld) {
      switch (fld.getDataType()) {
        case ReportDataTypes.dtNumber:
          {
            if (Iceutility.toLong(fieldData1) == Iceutility.toLong(fieldData2))
              return true;

            break;
          }
        case ReportDataTypes.dtBoolean:
          {
            if (Iceutility.toBoolean(fieldData1) == Iceutility.toBoolean(fieldData2))
              return true;

            break;
          }
        case ReportDataTypes.dtDouble:
          {
            if (Iceutility.toDouble(fieldData1) == Iceutility.toDouble(fieldData2))
              return true;

            break;
          }
        case ReportDataTypes.dtText:
          {
            let f1: string = fieldData1;
            let f2: string = fieldData2;
            if (f1.toLowerCase() == f2.toLowerCase())
              return true;

            break;
          }
        case ReportDataTypes.dtDateTime:
          {
            if (fieldData1 == fieldData2)
              return true;

            break;
          }
      }
    }
    return false;
  }

  private getTextWidth(ctx, text: string, font: string): TextMetrics {
    // re-use canvas object for better performance
    let save = ctx.font;
    ctx.font = font;
    ctx.font = save;
    return ctx.measureText(text);
  }

  getAlignmentOffset(ctx: CanvasRenderingContext2D, align: Report_Alignment, startrect: IceRect, text: string, font: string, fontSz: number, offset: number): IcePoint {
    let size: TextMetrics = this.getTextWidth(ctx, text, font);
    let left = startrect.left;
    let top = startrect.top + (fontSz / 2);
    switch (align) {
      case Report_Alignment.alignLeft:
        left = startrect.left + offset;
        break;
      case Report_Alignment.alignRight:
        left = startrect.right - size.width;
        break;
      case Report_Alignment.alignCenter:
        left = startrect.left + (startrect.right - startrect.left - size.width) / 2;
        break;
      case Report_Alignment.alignTop:
        top = startrect.top + 1 + offset;
        break;
      case Report_Alignment.alignBottom:
        top = startrect.bottom - (fontSz / 2) - 1 + offset;
        break;
      case Report_Alignment.alignMiddle:
        top = startrect.top + ((startrect.bottom - startrect.top - (fontSz / 2)) / 2) + offset;
        break;
      default:
        if (size.width >= startrect.right - startrect.left)
          left = startrect.left + offset;
        else
          left = startrect.left + offset;
        top = startrect.top + 1 + offset;
        break;
    }

    return new IcePoint(left, top);
  }

  onDraw(reportObj: report, reportCanvas: HTMLCanvasElement): void {
    if (reportObj == null || reportCanvas == null) return;

    let ctx: CanvasRenderingContext2D = reportCanvas.getContext("2d");
    let records = reportObj.outputRecords;

    let sysdata = reportObj.sysdata;
    sysdata.currentPageStarting = 0.0;
    sysdata.currentPageNumber = 1;
    // To do research how to make ppi and scale work
    debugger;
    let rcMargins: IceRect = reportObj.getMargins();
    let docSize = this.getDocumentSize(reportObj.getPaperSize());
    docSize.x = docSize.x - (rcMargins.left + rcMargins.right);
    docSize.y = docSize.y - (rcMargins.top + rcMargins.bottom);

    let width = screen.width, height = screen.height;
    let sqroot = (width * width) + +(height * height);
    let ppi = ((Math.sqrt(sqroot) / docSize.y) + (Math.sqrt(sqroot) / docSize.x)) / 2;
    ppi = 72;//this.getReportService().c_lnUnRealizedPixelPerInch;
    sysdata.pixelsPerInch = ppi;
    sysdata.scale = window.devicePixelRatio;
    sysdata.pageMax = 1;
    sysdata.currentRecordNumber = 1;
    sysdata.recordsPerPage = 0;
    sysdata.dateTime = Iceutility.GetLocalDate();

    sysdata.pageLimitY = 0;
    sysdata.currentPageY = 0;
    sysdata.pageFooterPrinted = false;

    sysdata.sort1Field = -1;
    sysdata.sort2Field = -1;
    sysdata.sort3Field = -1;

    let section: reportSection = reportObj.getSectionByType(ReportSectionTypes.secSortHeader1);
    if (section && section.getShow()) {
      sysdata.sort1Field = reportObj.getReportFieldIndex(section.getSortFullName());
      section = reportObj.getSectionByType(ReportSectionTypes.secSortHeader2);
      if (section && section.getShow()) {
        sysdata.sort2Field = reportObj.getReportFieldIndex(section.getSortFullName());
        section = reportObj.getSectionByType(ReportSectionTypes.secSortHeader3);
        if (section && section.getShow()) {
          sysdata.sort3Field = reportObj.getReportFieldIndex(section.getSortFullName());
        }
      }
    }

    let startingY: number = rcMargins.top;

    sysdata.pageLimitY = this.inch2Pix(docSize.y, sysdata.pixelsPerInch, sysdata.scale);

    reportCanvas.width = this.inch2Pix(docSize.x, sysdata.pixelsPerInch, sysdata.scale).toString();
    reportCanvas.height = this.inch2Pix(docSize.y, sysdata.pixelsPerInch, sysdata.scale).toString();

    if (sysdata.currentRecordNumber - 1 >= records.length)
      sysdata.currentRecordNumber = records.length - 1; // go back to the last record

    let printstack: reportSection[] = [];
    let secmap = {};
    secmap[ReportSectionTypes.secSortFooter1] = reportObj.getSectionByType(ReportSectionTypes.secSortFooter1);
    secmap[ReportSectionTypes.secSortFooter2] = reportObj.getSectionByType(ReportSectionTypes.secSortFooter2);
    secmap[ReportSectionTypes.secSortFooter3] = reportObj.getSectionByType(ReportSectionTypes.secSortFooter3);
    secmap[ReportSectionTypes.secDetail] = reportObj.getSectionByType(ReportSectionTypes.secDetail);
    secmap[ReportSectionTypes.secSortHeader3] = reportObj.getSectionByType(ReportSectionTypes.secSortHeader3);
    secmap[ReportSectionTypes.secSortHeader2] = reportObj.getSectionByType(ReportSectionTypes.secSortHeader2);
    secmap[ReportSectionTypes.secSortHeader1] = reportObj.getSectionByType(ReportSectionTypes.secSortHeader1);

    for (let secName in Object.keys(secmap)) {
      let repSec: reportSection = <reportSection>secmap[secName];
      if (repSec) repSec.setPrinted(false);
    }

    let first_rec = null;

    if (sysdata.currentPageNumber == 1) {
      sysdata.currentRecordNumber = 1;

      first_rec = records[sysdata.currentRecordNumber - 1];

      // print report header
      startingY = this.printReportHeader(reportObj, ctx, startingY, first_rec);

      startingY = this.printPageHeader(reportObj, ctx, startingY, first_rec);

      sysdata.sort1Field = reportObj.getReportFieldIndex(reportObj.getSectionByName(reportObj.getSectionNameByType(ReportSectionTypes.secSortHeader1)).getSortFullName());
      sysdata.sort2Field = reportObj.getReportFieldIndex(reportObj.getSectionByName(reportObj.getSectionNameByType(ReportSectionTypes.secSortHeader2)).getSortFullName());
      sysdata.sort3Field = reportObj.getReportFieldIndex(reportObj.getSectionByName(reportObj.getSectionNameByType(ReportSectionTypes.secSortHeader3)).getSortFullName());

      if (this.isItTimeForNewPage(reportObj, startingY)) {
        return;
      }
    }

    first_rec = null;
    let rec = first_rec = records[sysdata.currentRecordNumber - 1];
    if (rec == null) return;

    let prev_rec = rec;
    if (sysdata.currentRecordNumber > 1)
      prev_rec = records[sysdata.currentRecordNumber - 2]; // correct to prev record

    if (sysdata.currentPageNumber > 1) // do page header for pages above 1
    {
      // print page header
      startingY = this.printPageHeader(reportObj, ctx, startingY, rec);
    }

    let thisPageRecordCount = 0;
    do {
      if (sysdata.sort1Field >= 0 && this.complete(reportObj, sysdata.sort1Field, prev_rec[sysdata.sort1Field], rec[sysdata.sort1Field])) {
        printstack.push(reportObj.getSectionByType(ReportSectionTypes.secSortFooter1));
        for (let secName in Object.keys(secmap)) {
          let repSec: reportSection = <reportSection>secmap[secName];
          if (repSec) repSec.setPrinted(false);
        }
      }

      if (sysdata.sort2Field >= 0 && this.complete(reportObj, sysdata.sort2Field, prev_rec[sysdata.sort2Field], rec[sysdata.sort2Field])) {
        printstack.push(reportObj.getSectionByType(ReportSectionTypes.secSortFooter2));

        // To do check object before use
        reportObj.getSectionByType(ReportSectionTypes.secSortHeader2).setPrinted(false);
        reportObj.getSectionByType(ReportSectionTypes.secSortHeader3).setPrinted(false);

        reportObj.getSectionByType(ReportSectionTypes.secSortFooter2).setPrinted(false);
        reportObj.getSectionByType(ReportSectionTypes.secSortFooter3).setPrinted(false);
      }

      if (sysdata.sort3Field >= 0 && this.complete(reportObj, sysdata.sort3Field, prev_rec[sysdata.sort3Field], rec[sysdata.sort3Field])) {
        printstack.push(reportObj.getSectionByType(ReportSectionTypes.secSortFooter3));

        reportObj.getSectionByType(ReportSectionTypes.secSortHeader3).setPrinted(false);
        reportObj.getSectionByType(ReportSectionTypes.secSortFooter3).setPrinted(false);
      }

      // Print remaining sections
      while (printstack.length != 0) {
        let sec: reportSection = printstack.pop();
        if (this.isItTimeForNewPage(reportObj, startingY)) {
          break;
        }

        if (sec.isPrinted() == false) {
          startingY = this.printSection(reportObj, ctx, sec, startingY, prev_rec);
          sec.setPrinted(true);
        }
      }

      let secDtl = reportObj.getSectionByType(ReportSectionTypes.secDetail);
      printstack.push(secDtl); secDtl.setPrinted(false);

      if (sysdata.sort3Field >= 0)
        printstack.push(reportObj.getSectionByType(ReportSectionTypes.secSortHeader3));

      if (sysdata.sort2Field >= 0)
        printstack.push(reportObj.getSectionByType(ReportSectionTypes.secSortHeader2));

      if (sysdata.sort1Field >= 0)
        printstack.push(reportObj.getSectionByType(ReportSectionTypes.secSortHeader1));

      // Print the queue
      while (printstack.length != 0) {
        let sec: reportSection = printstack.pop();
        if (this.isItTimeForNewPage(reportObj, startingY)) {
          break;
        }

        if (sec.isPrinted() == false) {
          startingY = this.printSection(reportObj, ctx, sec, startingY, prev_rec);
          sec.setPrinted(true);
        }
      }

      if (this.isItTimeForNewPage(reportObj, startingY)) {
        break;
      }

      sysdata.currentRecordNumber++;

      // We will use this in the future to control the number of records per page by the user
      thisPageRecordCount++;
      if (thisPageRecordCount >= sysdata.recordsPerPage)
        break;

      prev_rec = rec;
    } while ((sysdata.currentRecordNumber - 1) < records.length
      && (rec = records[sysdata.currentRecordNumber - 1]) != null);

    // Print Remaining sections
    if (sysdata.sort1Field >= 0 && (
      this.complete(reportObj, sysdata.sort1Field, prev_rec[sysdata.sort1Field], rec[sysdata.sort1Field])
      || sysdata.currentPageNumber == sysdata.pageMax)) {
      printstack.push(reportObj.getSectionByType(ReportSectionTypes.secSortFooter1));
      for (let secName in Object.keys(secmap)) {
        let repSec: reportSection = <reportSection>secmap[secName];
        if (repSec) repSec.setPrinted(false);
      }
    }

    if (sysdata.sort2Field >= 0 && (
      this.complete(reportObj, sysdata.sort2Field, prev_rec[sysdata.sort2Field], rec[sysdata.sort2Field])
      || sysdata.currentPageNumber == sysdata.pageMax)) {
      printstack.push(reportObj.getSectionByType(ReportSectionTypes.secSortFooter2));

      reportObj.getSectionByType(ReportSectionTypes.secSortHeader2).setPrinted(false);
      reportObj.getSectionByType(ReportSectionTypes.secSortHeader3).setPrinted(false);

      reportObj.getSectionByType(ReportSectionTypes.secSortFooter2).setPrinted(false);
      reportObj.getSectionByType(ReportSectionTypes.secSortFooter3).setPrinted(false);
    }

    if (sysdata.sort3Field >= 0 && (
      this.complete(reportObj, sysdata.sort3Field, prev_rec[sysdata.sort3Field], rec[sysdata.sort3Field])
      || sysdata.currentPageNumber == sysdata.pageMax)) {
      printstack.push(reportObj.getSectionByType(ReportSectionTypes.secSortFooter3));

      reportObj.getSectionByType(ReportSectionTypes.secSortHeader3).setPrinted(false);
      reportObj.getSectionByType(ReportSectionTypes.secSortFooter3).setPrinted(false);
    }

    // Print remaining sections
    while (printstack.length != 0) {
      let sec: reportSection = printstack.pop();
      if (this.isItTimeForNewPage(reportObj, startingY)) {
        break;
      }

      if (sec.isPrinted() == false) {
        startingY = this.printSection(reportObj, ctx, sec, startingY, prev_rec);
        sec.setPrinted(true);
      }
    }

    if (sysdata.currentPageNumber == sysdata.pageMax) {
      if (this.isItTimeForNewPage(reportObj, startingY)) {
        return;
      }

      startingY = this.printReportFooter(reportObj, ctx, startingY, prev_rec);
      startingY = this.printPageFooter(reportObj, ctx, startingY, prev_rec);
    }
    else {
      if (this.isItTimeForNewPage(reportObj, startingY)) {
        return;
      }

      startingY = this.printPageFooter(reportObj, ctx, startingY, prev_rec);
    }

    // To do
    //for (int isum = 0; isum < this.reportObj.getSumFieldCount(); isum++ )
    //{
    //  CReportSummationField * pSumField = pRW -> GetSumField(isum);
    //  if (pSumField) {
    //    // reset values
    //    pSumField -> SetHeaderValue(COleVariant((double)0.0));
    //    pSumField -> SetDetailValue(COleVariant((double)0.0));
    //    pSumField -> SetFooterValue(COleVariant((double)0.0));

    //    pSumField -> SetHeaderValue(COleVariant((long)0));
    //    pSumField -> SetDetailValue(COleVariant((long)0));
    //    pSumField -> SetFooterValue(COleVariant((long)0));
    //  }
    //}
  }

  doSection(reportObj: report, ctx: CanvasRenderingContext2D, sectionStart: number, section: reportSection, rec: any[]) {
    let currentY = sectionStart;
    let lastCurrentY = currentY;

    if (this.isItTimeForNewPage(reportObj, currentY + section.getHeight())) {
      console.log("New Page Needed.");
    }

    for (let y = 0.0; y <= section.getHeight(); y += this.c_dblDEFAULTCELL_Y) {
      let objs = section.getObjectsByY(y);

      // print section
      for (let i = 0; i < objs.length; i++) {
        let obj: reportObject = objs[i];
        if (obj) {
          switch (obj.getType()) {
            case ReportObjectType.otNone:
              break;
            case ReportObjectType.otObject: {
              let y_Returned = this.doFormatObject(reportObj, ctx, currentY + y, obj, true);
              if (y_Returned > lastCurrentY)
                lastCurrentY = y_Returned;
              }
              break;
            case ReportObjectType.otTextField: {
              let fld: reportTextField = <reportTextField>obj;
              let y_Returned = this.doFormat(reportObj, ctx, currentY + y, fld, true);
              if (y_Returned > lastCurrentY)
                lastCurrentY = y_Returned;
              }
              break;
            case ReportObjectType.otDataField: {
                if (null != rec) {
                  let fld: reportDataField = <reportDataField>obj;
                  let iFieldIndex = reportObj.getReportFieldIndex(fld.getFullName());
                  if (iFieldIndex >= 0 && iFieldIndex < rec.length) {
                    fld.setData(rec[iFieldIndex]);
                    //To do: however selection fields and report fields may end up being the same
                    //CReportDataField selectionFld;
                    //if (pRW -> GetSelectionField(pFld -> GetFullName(), selectionFld)) {
                    //  pFld -> SetHandWriting(selectionFld.GetHandWriting());
                    //}

                    let y_Returned = this.doFormatData(reportObj, ctx, currentY + y, fld, true);
                    if (y_Returned > lastCurrentY)
                      lastCurrentY = y_Returned;
                  }
                  fld.setData("");
                }
              }
              break;
            case ReportObjectType.otCompanyField: {
              //To do
              //  STRING_ARRAY & comprec = pRW -> GetCompanyRecord();
              //  CReportCompanyField * pFld = (CReportCompanyField *)pObj;
              //  int iFieldIndex = pRW -> GetReportCompanyFieldIndex(pFld -> GetFullName());
              //  if (iFieldIndex >= 0 && iFieldIndex < comprec.GetSize()) {
              //    pFld -> SetData(comprec.GetAt(iFieldIndex));
              //    CReportCompanyField selectionFld;
              //    if (pRW -> GetCompanySelectionField(pFld -> GetFullName(), selectionFld)) {
              //      pFld -> SetHandWriting(selectionFld.GetHandWriting());
              //    }

              //    double dblY_Returned = DoFormat(pdc, dblCurrentY + dblY/*pObj->GetY()*/, pFld);
              //    if (dblY_Returned > dblLastCurrentY)
              //      dblLastCurrentY = dblY_Returned;
              //  }
              //  pFld -> SetData(_T(""));
              }
              break;
            case ReportObjectType.otCustomField: {
              if (null != rec) {
                  // To do
                  //  CReportCustomField * pFld = (CReportCustomField *)pObj;
                  //  int iFieldIndex = pRW -> GetReportFieldIndex(pFld -> GetFullName());
                  //  if (iFieldIndex >= 0 && iFieldIndex < prec -> GetSize()) {
                  //    pFld -> SetData(prec -> GetAt(iFieldIndex));
                  //    CReportCustomField selectionFld;
                  //    if (pRW -> GetCustomSelectionField(pFld -> GetFullName(), selectionFld)) {
                  //      pFld -> SetHandWriting(selectionFld.GetHandWriting());
                  //    }

                  //    double dblY_Returned = DoFormat(pdc, dblCurrentY + dblY/*pObj->GetY()*/, pFld);
                  //    if (dblY_Returned > dblLastCurrentY)
                  //      dblLastCurrentY = dblY_Returned;
                  //  }
                  //  pFld -> SetData(_T(""));
                }
              }
              break;
            case ReportObjectType.otSystemField: {
              let fld: reportSystemField = <reportSystemField>obj;
              let sysdata = reportObj.sysdata;
              let data: string = "";
              switch (fld.getSystem()) {
                case ReportSystemFieldTypes.sysNone:
                  break;
                case ReportSystemFieldTypes.sysPageNumber:
                  data = sysdata.currentPageNumber.toString();
                  break;
                case ReportSystemFieldTypes.sysRecordCount:
                  data = sysdata.currentRecordNumber.toString();
                  break;
                case ReportSystemFieldTypes.sysDate:
                  data = Iceutility.getDateTextEx(sysdata.dateTime, null, false);
                  break;
                case ReportSystemFieldTypes.sysTime:
                  data = Iceutility.getDateTextEx(sysdata.dateTime, null, true);
                  break;
                case ReportSystemFieldTypes.sysDateTime:
                  data = Iceutility.getDateTextEx(sysdata.dateTime, null, true);
                  break;
              }

              fld.setData(data);
              let y_Returned = this.doFormat(reportObj, ctx, currentY + y, fld, true);
              if (y_Returned > lastCurrentY)
                lastCurrentY = y_Returned;

              fld.setData("");
            }
              break;
            case ReportObjectType.otParameterField:
              break;
            case ReportObjectType.otFormulaField: {
              // To do
              //  CReportEvaluator & eval = pDoc -> GetReportEvaluator();
              //  CReportFormulaField * pFld = (CReportFormulaField *)pObj;
              //  int nIndex = pRW -> GetFormulaIndex(pFld -> GetColumnName());
              //  if (nIndex >= 0) {
              //    CReportFormulaField * pMainFormulaField = pRW -> GetFormula(nIndex);
              //    if (pMainFormulaField) {
              //      double val = 0.0;
              //      CString strFormula = pMainFormulaField -> GetFormula();

              //      CString strData;
              //      eval.evaluate(strFormula, strData);

              //      val = atof((const char*)strData.GetString());
              //      strData.Format(_T("%.2lf"), val);

              //      pFld -> SetData(strData);
              //      double dblY_Returned = DoFormat(pdc, dblCurrentY + dblY/*pObj->GetY()*/, pFld);
              //      if (dblY_Returned > dblLastCurrentY)
              //        dblLastCurrentY = dblY_Returned;

              //      pFld -> SetData(_T(""));
              //    }
              //  }
              }
              break;
            case ReportObjectType.otSummationField: {
              let fld: reportSummationField = <reportSummationField>obj;
              // To do
              //     let nSumFldIndex = pRW -> GetSumFieldIndex(pFld -> GetFullName());
              //     if (nSumFldIndex >= 0) {
              //       CReportSummationField * pStoredSumFld = pRW -> GetSumField(nSumFldIndex);
              //       if (pStoredSumFld) {

              //         COleVariant var ;
              //:: VariantInit(&var);

              //         switch (pRW -> GetSectionType(pSection)) {
              //           case CReportWriter:: secReportHeader:
              //case CReportWriter:: secPageHeader:
              //case CReportWriter:: secSortHeader1:
              //case CReportWriter:: secSortHeader2:
              //case CReportWriter:: secSortHeader3:
              //             {
              //               var = pStoredSumFld-> GetHeaderValue();
              //               break;
              //             }
              //           case CReportWriter:: secDetail:
              //             {
              //               var = pStoredSumFld-> GetDetailValue();
              //               break;
              //             }
              //           case CReportWriter:: secSortFooter1:
              //case CReportWriter:: secSortFooter2:
              //case CReportWriter:: secSortFooter3:
              //case CReportWriter:: secPageFooter:
              //case CReportWriter:: secReportFooter:
              //             {
              //               var = pStoredSumFld-> GetFooterValue();
              //               break;
              //             }
              //         }

              //         CString strData;
              //         switch (pStoredSumFld -> GetDataType()) {
              //           case CColumnData:: dtypeNumber:
              //             {
              //               strData.Format(_T("%ld"), V_I4(&var));
              //               break;
              //             }
              //           case CColumnData:: dtypeFloat:
              //             {
              //               strData.Format(_T("%.2lf"), V_R8(&var));
              //               break;
              //             }
              //         }

              //         pFld -> SetData(strData);
              //         double dblY_Returned = DoFormat(pdc, dblCurrentY + dblY/*pObj->GetY()*/, pFld);
              //         if (dblY_Returned > dblLastCurrentY)
              //           dblLastCurrentY = dblY_Returned;

              //         pFld -> SetData(_T(""));
              //       }
              //     }
              }
              break;
            case ReportObjectType.otPicture:
              break;
          }
        }
      } // each object
    } // each line

    if (lastCurrentY > currentY)
      currentY = lastCurrentY;

    return currentY + this.c_dblDEFAULTPADDING_Y;
  }

  doFormat(reportObj: report, ctx: CanvasRenderingContext2D, y: number, fld: reportField, coordinate: boolean): number {
    let handwritingY = y;
    if (null == fld)
      return -1.0;

    let sysdata = reportObj.sysdata;
    let rcMargins: IceRect = reportObj.getMargins();
    let leftOffset: number = 0.0;
    if (coordinate == false)
      leftOffset = rcMargins.left;

    let rect: IceRect = new IceRect(0, 0, 0, 0);
    rect.left = this.inch2Pix(leftOffset + fld.getX(), sysdata.pixelsPerInch, sysdata.scale);
    rect.top = this.inch2Pix(y, sysdata.pixelsPerInch, sysdata.scale);
    rect.right = this.inch2Pix(leftOffset + fld.getX() + fld.getWidth(), sysdata.pixelsPerInch, sysdata.scale);
    rect.bottom = this.inch2Pix(y + fld.getHeight(), sysdata.pixelsPerInch, sysdata.scale);

    // To do
    //pdc -> SetBkMode(TRANSPARENT);
    //To do once Multimedia service is done
    //if (fld.getHandWriting()) {
    //  let grfPages: IcegraffitiPage[] = this.grafittiSvc.deserialize(fld.getData());
    //  if (grfPages && grfPages.length > 0) {
    //    let theLowestPoint: IcePoint = new IcePoint();//this.grafittiSvc.draw(ctx, grfPages, rect, false);
    //    handwritingY += this.pix2Inch(theLowestPoint.y, sysdata.pixelsPerInch, sysdata.scale);
    //    this.doBorder(ctx, fld, rect);
    //    return handwritingY;
    //  }
    //}

    let text = fld.getData();
    // check to see if we're complete
    if (text == null || text.length == 0) {
      this.doBorder(ctx, fld, rect);
      y = this.pix2Inch(rect.bottom, sysdata.pixelsPerInch, sysdata.scale);
      return y;
    }

    // To do
    switch (fld.getDataType()) {
      case ReportDataTypes.dtNumber:
        {
          // do nothing for now later we will add the format mask
          //long nTextEquivelent = atol(strText);
          //strText.Format(_T("%ld"), nTextEquivelent);
          break;
        }
      case ReportDataTypes.dtDouble:
        {
          // do nothing for now later we will add the format mask
          //double dblTextEquivelent = atof(strText);
          //strText.Format(_T("%lf"), dblTextEquivelent);
          break;
        }
      case ReportDataTypes.dtText:
        break;
      case ReportDataTypes.dtDateTime:
        break;
      case ReportDataTypes.dtBoolean:
        {
          //long nTextEquivelent = atol((const char*)strText.GetString());
          //strText.Format(_T("%s"), nTextEquivelent == TRUE ? _T("Yes") : _T("No"));
          break;
        }
      default:
        break;
    }

    let align = fld.getAlignType();
    let size: IcePoint = new IcePoint(0, 0); // Substitute IcePoint for CSize
    if (fld.getWrap()) {
      // To do
      //  CStringArray arLines;
      //  long nTop = rect.top;
      //  long nGreatestLineLength = 0;
      //  CColumnData:: BreakIntoLines(pdc, strText, * lpLogFont, pFld -> GetWidth(), arLines, nGreatestLineLength);
      //  for (int nLine = 0; nLine < arLines.GetSize(); nLine++ )
      //  {
      //    CString strLine = arLines.GetAt(nLine);
      //    if (nTop > rect.bottom)
      //      break; // don't draw outside of rect.

      //    CPoint ptAlignOffset = GetAlignmentOffset(pdc, dwAlign, CRect(rect.left, nTop, rect.right, rect.bottom), strLine, 2);
      //    pdc -> TextOut(ptAlignOffset.x, ptAlignOffset.y/*rect.left, nTop*/, strLine);
      //    size = pdc -> GetTextExtent(strLine);
      //    nTop += size.cy;
      //    nTop += (size.cy / 2);
      //  }
      //  size.cy = nTop;
      //  //size.cy = pdc->DrawText(strText, &rect, DT_LEFT | DT_EXTERNALLEADING);			

      // To do we will need update the bottom calc
      //y += repSvc.pix2Inch(rect.bottom/*size.y*/, sysdata.pixelsPerInch, sysdata.scale);
      //this.doBorder(ctx, fld, rect);
    }
    else {
      let fontInches = Iceutility.toLong(fld.getFontSize()) / this.c_dblFontPointsPerInch;
      let fontPixels = this.inch2Pix(fontInches, sysdata.pixelsPerInch, sysdata.scale);
      let fontToUse = [fld.getFontItalic() ? "Italic" : "", fld.getFontBold() ? "Bold" : "", fontPixels.toString() + "px", fld.getFontName()]
      let ptAlignOffset = this.getAlignmentOffset(ctx, align, rect, text, fontToUse.join(' '), Iceutility.toLong(fld.getFontSize()), 2);
      let saveFont = ctx.font;
      ctx.font = fontToUse.join(' ');//'12px sans-serif'; // This should come from fld
      ctx.strokeStyle = "black";
      ctx.fillText(text, ptAlignOffset.x, ptAlignOffset.y/*rect.left, rect.top*/);
      ctx.font = saveFont;
      size = ptAlignOffset;
      this.doBorder(ctx, fld, rect);
    }

    return y;
  }

  doFormatData(reportObj: report, ctx: CanvasRenderingContext2D, y: number, fld: reportDataField, coordinate: boolean): number {
    let bcType: ReportBarCodeType = fld.getBarCodeType();
    if (bcType == ReportBarCodeType.TypeNone) {
      y = this.doFormat(reportObj, ctx, y, fld, coordinate);
    }
    else {
      let barcodeY: number = y;
      let objectY: number = this.doFormat(reportObj, ctx, y, fld, true);

      let strFileName = "", strBitmapName = "", data = fld.getData();
      if (data == null || data.length == 0) return objectY;
      ////////////
      // To do
      //   if (CColumnData:: CreateTemporaryFile("c:\\temp", strData, strFileName))
      //   {
      //     static CString barcodePath = "";
      //     static CString path = CBaseLog:: GetAppPath() + "RW.ini";

      //     if (barcodePath.IsEmpty()) {
      //       const size_t bufSize = 1024;
      //       TCHAR buffer[bufSize + 1];
      //       memset(buffer, 0, bufSize + 1);
      //       int ret = GetPrivateProfileString("CONFIG", "BarcodeApp", "", buffer, bufSize, path);
      //       if (!ret) {
      //         barcodePath = ".\\RWBarcode.exe";
      //       }
      //       else {
      //         barcodePath = buffer;
      //       }
      //     }

      //     strBitmapName = strFileName + ".bmp";
      //     CString strCmdLine; strCmdLine.Format("-in \"%s\" -properties \"type=%s text=%ld\" -out \"%s\"", strFileName, CReportDataField:: GetBarCodeTypeCommand(bcType), pFld -> GetBarCodeShowText(), strBitmapName);

      //     #if true
      //     if (FALSE == LaunchProcess(barcodePath, strCmdLine)) {
      //       DWORD dw = GetLastError();
      //       AfxMessageBox(std:: string("Failed to Launch Barcode").c_str(), MB_OK);
      //     }
      //     #else
      //     std:: string props = std:: string("type=") + std:: string(CReportDataField:: GetBarCodeTypeCommand(bcType).GetString()) + std:: string(" text=") + std:: string(pFld -> GetBarCodeShowText() ? "1" : "0");
      //     bar.NewBarCode(m_hWnd, std:: string(strData.GetString()), props, std:: string(strFileName + ".bmp"));
      //     #endif
      //   }

      //   CBitmap * pBmp = pFld -> GetBarCodeBitmap();
      //   pBmp -> DeleteObject();

      //   if (pdc -> m_hDC == NULL) {
      //     TRACE("Error condition.\n");
      //   }

      //   #if true
      //   HBITMAP hBitmap = (HBITMAP):: LoadImage(NULL, strBitmapName, IMAGE_BITMAP, 0, 0, LR_LOADFROMFILE);
      //   #else
      //   HBITMAP hBitmap = NULL;
      //   #endif

      //   if (pdc -> m_hDC == NULL) {
      //     TRACE("Error condition.\n");
      //   }

      //   //if (pBmp->LoadBitmap(CReportDataField::GetBarCodeTypeText(pFld->GetBarCodeType()))) {
      //   if (hBitmap != NULL) {
      //     pBmp -> Attach(hBitmap);

      //     // To do: Replace this code with DoImage(...)
      //     CReportWriter * pRW = pDoc -> GetReportWriter();
      //     if (NULL == pRW)
      //       return -1.0;

      //     CRect & rcMargins = pRW -> GetMargins();
      //     double dblLeftOffset = 0.0;
      //     if (bCoordinate == FALSE)
      //       dblLeftOffset = ((double)rcMargins.left / (double)c_lnUnRealizedPixelPerInch);

      //     CRect rect;
      //     rect.left = CColumnData:: Inch2Pix(dblLeftOffset + pFld -> GetX(), pdc -> GetDeviceCaps(LOGPIXELSX), c_dblUnRealizedScale);
      //     rect.top = CColumnData:: Inch2Pix(dblY, pdc -> GetDeviceCaps(LOGPIXELSY), c_dblUnRealizedScale);
      //     rect.right = CColumnData:: Inch2Pix(dblLeftOffset + pFld -> GetX() + pFld -> GetWidth(), pdc -> GetDeviceCaps(LOGPIXELSX), c_dblUnRealizedScale);
      //     rect.bottom = CColumnData:: Inch2Pix(dblY + pFld -> GetHeight(), pdc -> GetDeviceCaps(LOGPIXELSY), c_dblUnRealizedScale);

      //     if (bcType != CReportDataField:: Standard1D_Code128 && bcType != CReportDataField:: Standard1D_Code39 && bcType != CReportDataField:: Standard1D_Code93)
      //     rect.DeflateRect(15, 15, 15, 15);

      //     if (pdc -> m_hDC == NULL) {
      //       TRACE("Error condition.\n");
      //     }

      //     CDC memDC;
      //     if (memDC.CreateCompatibleDC(pdc)) {
      //       DIBSECTION dibsection;
      //       pBmp -> GetObject(sizeof(DIBSECTION), & dibsection);

      //       memDC.SelectObject(pBmp);

      //       if (pdc -> m_hDC == NULL) {
      //         TRACE("Error condition.\n");
      //       }

      //       int OldBackMode = memDC.SetBkMode(TRANSPARENT);
      //       if (pFld -> GetScaleToFit()) {
      //         pdc -> StretchBlt(
      //           rect.left, rect.top, rect.Width(), rect.Height(), //rect.Height(), do the full height
      //			      & memDC, 0, 0, dibsection.dsBm.bmWidth, dibsection.dsBm.bmHeight, SRCCOPY);
      //       }
      //       //else if (dibsection.dsBm.bmHeight > rect.Height()
      //       //	&& dibsection.dsBm.bmWidth > rect.Width()) // no tolerance here because we know we must scale down
      //       //													// and the code above filtered out the over tolerance data
      //       //{
      //       //	pdc->StretchBlt(
      //       //		rect.left, rect.top, rect.Width(), rect.Height(), //rect.Height(), do the full height
      //       //		&memDC, 0, 0, dibsection.dsBm.bmWidth, dibsection.dsBm.bmHeight, SRCCOPY);
      //       //}
      //       else {
      //         pdc -> BitBlt(rect.left, rect.top,
      //           dibsection.dsBm.bmWidth,
      //           dibsection.dsBm.bmHeight,
      //			      & memDC, 0, 0, SRCCOPY);
      //       }
      //       memDC.SetBkMode(OldBackMode);
      //       memDC.DeleteDC();

      //       barcodeY += pFld -> GetHeight(); // Let's use the barcode height instead of the dibsection.dsBm.bmHeight
      //     }
      //   }

      //   CFile:: Remove(strFileName);
      //   CFile:: Remove(strBitmapName);

      //   dblY = max(objectY, barcodeY);
      //   dblY = max(dblY, pFld -> GetHeight());
    }
    return y;
  }

  doFormatObject(reportObj: report, ctx: CanvasRenderingContext2D, y: number, obj: reportObject, setBackColor: boolean): number {
    if (null == obj)
      return -1.0;

    // To do
    //pdc -> SetBkMode(TRANSPARENT);

    let rcMargins: IceRect = reportObj.getMargins();
    let leftOffset: number = rcMargins.left;

    let sysdata = reportObj.sysdata;
    let rect: IceRect = new IceRect();
    rect.left = this.inch2Pix(leftOffset + obj.getX(), sysdata.pixelsPerInch, sysdata.scale);
    rect.top = this.inch2Pix(y, sysdata.pixelsPerInch, sysdata.scale);
    rect.right = this.inch2Pix(leftOffset + obj.getX() + obj.getWidth(), sysdata.pixelsPerInch, sysdata.scale);
    rect.bottom = this.inch2Pix(y + obj.getHeight(), sysdata.pixelsPerInch, sysdata.scale);

    // background color
    if (setBackColor) {
      let saveFillStyle = ctx.fillStyle;
      // To do
      //ctx.fillStyle = "green"; convert to RGB to HEX
      /*
      function componentToHex(c) {
        var hex = c.toString(16);
        return hex.length == 1 ? "0" + hex : hex;
      }

      function rgbToHex(r, g, b) {
        return "#" + componentToHex(r) + componentToHex(g) + componentToHex(b);
      }
      */
      ctx.fillRect(rect.left, rect.top, rect.right - rect.left, rect.bottom - rect.top);
      ctx.fillStyle = saveFillStyle;
    }

    /*
    // To do base64 image
    double imageY = -1.0;
    CString imageData = pObj -> GetImageData();
    if (!imageData.IsEmpty()) {
      CByteArray bytes;
      if (CColumnData:: Base64Decode("Base64", imageData, bytes))
      {
        CBitmap * pBmp = pObj -> GetImage();
        pBmp -> DeleteObject();

        CString strFileName;
        if (CColumnData:: GetTemporaryFileName("c:\\temp", strFileName))
        {
          strFileName += ".bmp";
          if (CColumnData:: WriteFile(strFileName, bytes))
          {
            HBITMAP phBitmap = (HBITMAP)LoadImage(NULL, strFileName, IMAGE_BITMAP, 0, 0,
              LR_CREATEDIBSECTION | LR_DEFAULTSIZE | LR_LOADFROMFILE);
            if (phBitmap != NULL) {
              pBmp -> Attach(phBitmap);

              imageY = DoImage(pdc, dblY, pObj, pBmp, FALSE);
              CFile:: Remove(strFileName);
            }
          }
        }
      }
    }
    */
    this.doBorder(ctx, obj, rect);
    y += obj.getHeight();
    return y;
  }

  doBorder(ctx: CanvasRenderingContext2D, obj: reportObject, rect: IceRect): void {
    if (obj == null)
      return;

    // Border stuff
    let borders: IceRect = obj.getBorders();
    let borderStyle: Report_Border_Style = obj.getBorderStyle();
    let borderColor: string = obj.getBackColor();
    // To do set ctx
    this.doRect(ctx, borders, rect);
    // To do restore ctx
  }

  doRect(ctx: CanvasRenderingContext2D, borders: IceRect, rect: IceRect): void {
    rect.deflateRect(0, 5, 0, 0);
    rect.inflateRect(0, 0, 0, 5);
    let borderlineoffset = 0;
    ctx.beginPath();
    if (borders.left == 1) {
      ctx.moveTo(rect.left + borderlineoffset, rect.top + borderlineoffset);
      ctx.lineTo(rect.left + borderlineoffset, rect.bottom + borderlineoffset);
    }

    if (borders.right == 1) {
      ctx.moveTo(rect.right + borderlineoffset, rect.top + borderlineoffset);
      ctx.lineTo(rect.right + borderlineoffset, rect.bottom + borderlineoffset);
    }

    if (borders.top == 1) {
      ctx.moveTo(rect.left + borderlineoffset, rect.top + borderlineoffset);
      ctx.lineTo(rect.right + borderlineoffset, rect.top + borderlineoffset);
    }

    if (borders.bottom == 1) {
      ctx.moveTo(rect.left + borderlineoffset, rect.bottom + borderlineoffset);
      ctx.lineTo(rect.right + borderlineoffset, rect.bottom + borderlineoffset);
    }

    ctx.closePath();
    ctx.stroke();
    rect.inflateRect(0, 5, 0, 0);
    rect.deflateRect(0, 0, 0, 5);
  }

  printReportHeader(reportObj: report, ctx: CanvasRenderingContext2D, sectionStart: number, rec: any[]): number {
    // insert items into report
    let section: reportSection = reportObj.getSectionByType(ReportSectionTypes.secReportHeader);
    if (section && section.objects.length > 0) {
      // calc summations
      //UpdateSumFields(CReportWriter:: secReportHeader, prec);

      // skip the display
      if (false == section.getShow())
        return sectionStart;

      sectionStart = this.doSection(reportObj, ctx, sectionStart, section, rec);
      return sectionStart;
    }

    return sectionStart;
  }

  printPageHeader(reportObj: report, ctx: CanvasRenderingContext2D, sectionStart: number, rec: any[]): number {
    // insert items into report
    let section: reportSection = reportObj.getSectionByType(ReportSectionTypes.secPageHeader);
    if (section && section.objects.length > 0) {
      // calc summations
      //UpdateSumFields(CReportWriter:: secPageHeader, prec);

      // skip the display
      if (false == section.getShow())
        return sectionStart;

      sectionStart = this.doSection(reportObj, ctx, sectionStart, section, rec);
      return sectionStart;
    }

    return sectionStart;
  }

  printSortHeader1(reportObj: report, ctx: CanvasRenderingContext2D, sectionStart: number, rec: any[]): number {
    // insert items into report
    let section: reportSection = reportObj.getSectionByType(ReportSectionTypes.secSortHeader1);
    if (section) {
      // calc summations
      //UpdateSumFields(CReportWriter:: secSortHeader1, prec);

      // skip the display
      if (false == section.getShow())
        return sectionStart;

      sectionStart = this.doSection(reportObj, ctx, sectionStart, section, rec);
      return sectionStart;
    }

    return -1.0;
  }

  printSortHeader2(reportObj: report, ctx: CanvasRenderingContext2D, sectionStart: number, rec: any[]): number {
    // insert items into report
    let section: reportSection = reportObj.getSectionByType(ReportSectionTypes.secSortHeader2);
    if (section) {
      // calc summations
      //UpdateSumFields(CReportWriter:: secSortHeader2, prec);

      // skip the display
      if (false == section.getShow())
        return sectionStart;

      sectionStart = this.doSection(reportObj, ctx, sectionStart, section, rec);
      return sectionStart;
    }

    return -1.0;
  }

  printSortHeader3(reportObj: report, ctx: CanvasRenderingContext2D, sectionStart: number, rec: any[]): number {
    // insert items into report
    let section: reportSection = reportObj.getSectionByType(ReportSectionTypes.secSortHeader3);
    if (section) {
      // calc summations
      //UpdateSumFields(CReportWriter:: secSortHeader3, prec);

      // skip the display
      if (false == section.getShow())
        return sectionStart;

      sectionStart = this.doSection(reportObj, ctx, sectionStart, section, rec);
      return sectionStart;
    }

    return -1.0;
  }

  printDetail(reportObj: report, ctx: CanvasRenderingContext2D, sectionStart: number, rec: any[]): number {
    // insert items into report
    let section: reportSection = reportObj.getSectionByType(ReportSectionTypes.secDetail);
    if (section) {
      // calc summations
      //UpdateSumFields(CReportWriter:: secDetail, prec);

      // skip the display
      if (false == section.getShow())
        return sectionStart;

      sectionStart = this.doSection(reportObj, ctx, sectionStart, section, rec);
      return sectionStart;
    }

    return -1.0;
  }

  printPageFooter(reportObj: report, ctx: CanvasRenderingContext2D, sectionStart: number, rec: any[]): number {
    // insert items into report
    let section: reportSection = reportObj.getSectionByType(ReportSectionTypes.secPageFooter);
    if (section && section.objects.length > 0) {
      // calc summations
      //UpdateSumFields(CReportWriter:: secPageFooter, prec);

      // skip the display
      if (false == section.getShow())
        return sectionStart;

      let sysdata = reportObj.sysdata;
      sectionStart = this.pix2Inch(sysdata.pageLimitY, sysdata.pixelsPerInch, sysdata.scale);
      sectionStart = this.doSection(reportObj, ctx, sectionStart, section, rec);
      return sectionStart;
    }

    return sectionStart;
  }

  printSortFooter1(reportObj: report, ctx: CanvasRenderingContext2D, sectionStart: number, rec: any[]): number {
    // insert items into report
    let section: reportSection = reportObj.getSectionByType(ReportSectionTypes.secSortFooter1);
    if (section) {
      // calc summations
      //UpdateSumFields(CReportWriter:: secSortFooter1, prec);

      // skip the display
      if (false == section.getShow())
        return sectionStart;

      sectionStart = this.doSection(reportObj, ctx, sectionStart, section, rec);
      return sectionStart;
    }

    return -1.0;
  }

  printSortFooter2(reportObj: report, ctx: CanvasRenderingContext2D, sectionStart: number, rec: any[]): number {
    // insert items into report
    let section: reportSection = reportObj.getSectionByType(ReportSectionTypes.secSortFooter2);
    if (section) {
      // calc summations
      //UpdateSumFields(CReportWriter:: secSortFooter2, prec);

      // skip the display
      if (false == section.getShow())
        return sectionStart;

      sectionStart = this.doSection(reportObj, ctx, sectionStart, section, rec);
      return sectionStart;
    }

    return -1.0;
  }

  printSortFooter3(reportObj: report, ctx: CanvasRenderingContext2D, sectionStart: number, rec: any[]): number {
    // insert items into report
    let section: reportSection = reportObj.getSectionByType(ReportSectionTypes.secSortFooter3);
    if (section) {
      // calc summations
      //UpdateSumFields(CReportWriter:: secSortFooter3, prec);

      // skip the display
      if (false == section.getShow())
        return sectionStart;

      sectionStart = this.doSection(reportObj, ctx, sectionStart, section, rec);
      return sectionStart;
    }

    return -1.0;
  }

  printReportFooter(reportObj: report, ctx: CanvasRenderingContext2D, sectionStart: number, rec: any[]): number {
    // insert items into report
    let section: reportSection = reportObj.getSectionByType(ReportSectionTypes.secReportFooter);
    if (section) {
      // calc summations
      //UpdateSumFields(CReportWriter:: secReportFooter, prec);

      // skip the display
      if (false == section.getShow())
        return sectionStart;

      sectionStart = this.doSection(reportObj, ctx, sectionStart, section, rec);
      return sectionStart;
    }

    return -1.0;
  }

  printSection(reportObj: report, ctx: CanvasRenderingContext2D, sec: reportSection, sectionStart: number, rec: any[]) {
    if (sec == null) return -1.0;

    // insert items into report
    // calc summations
    //UpdateSumFields(sortfield, prec);

    // skip the display
    if (false == sec.getShow())
      return sectionStart;

    sectionStart = this.doSection(reportObj, ctx, sectionStart, sec, rec);
    sec.setPrinted(true);
    return sectionStart;
  }
}
